import { AlertOptions, useAppAlert } from '@/app/AppAlertProvider.tsx'
import heic2any from 'heic2any'
import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import {
  CameraIconBlack,
  CameraSVG,
  FileUploadIcon,
  SparkleIcon,
  StackIcon,
} from '../../icons/general'
import { Button } from '../Button'
import LoadingCard from '../LoadingCard'
import { ChildrenProp } from '@/common/types_and_interfaces.ts'
import { cn } from '@/common/utilities.ts'
import isMobile from 'is-mobile'

interface Props {
  onUpload: (files: File[]) => void
  maxSize?: number
  allowMultiple?: boolean
  uploadInformation?: React.ReactNode
  isPdfAllowed?: boolean
}

const baseAlertConfig: AlertOptions = {
  variant: 'error',
  confirmBtn: true,
  confirmBtnText: 'OK',
}

const FIVE_MEGABYTES = 5 * 1024 * 1024

type MobileButtonProps = {
  size?: 'md'
} & ChildrenProp
const MobileButton = ({ children, size = 'md' }: MobileButtonProps) => {
  const baseClasses = 'font-semibold inline-flex justify-center items-center'
  const variantClasses =
    'bg-primary-600 text-white disabled:cursor-not-allowed hover:bg-primary-500 disabled:bg-gray-200 disabled:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
  const sizeClasses = {
    md: 'rounded-full p-2',
  }
  return (
    <button
      className={cn(baseClasses, variantClasses, sizeClasses[size])}
      type="button"
    >
      {children}
    </button>
  )
}
const BASE_FORMATS = ['.jpg', '.jpeg', '.png', '.heic'] as const

function DocumentUploadNew({
  onUpload,
  maxSize = FIVE_MEGABYTES,
  allowMultiple: allowMultiple = true,
  uploadInformation,
  isPdfAllowed = false,
}: Props) {
  const { t: translate } = useTranslation()
  const { fireAlert } = useAppAlert()
  const [isLoadingHeic, setIsLoadingHeic] = useState(false)

  const convertHeicToJpeg = async (file: File) => {
    setIsLoadingHeic(true)
    try {
      const convertedBlob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.8,
      })

      const convertedFile = new File(
        [convertedBlob as Blob],
        file.name.replace(/\.heic$/i, '.jpg'),
        { type: 'image/jpeg' }
      )

      return convertedFile
    } catch (error) {
      fireAlert({
        variant: 'error',
        message: translate('failed_to_convert_heic_image'),
        confirmBtn: true,
        confirmBtnText: translate('ok'),
      })
    } finally {
      setIsLoadingHeic(false)
    }
  }

  function getAcceptedFormats() {

    if (isPdfAllowed) {
      return [...BASE_FORMATS, '.pdf']
    } else {
      return BASE_FORMATS
    }
  }
  

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {

      if(acceptedFiles.length === 0) { 
        fireAlert({
          variant: 'error',
          message: `${translate("failed_to_upload")} ${getAcceptedFormats().join(', ')}.`,
        })

        return
      }


      try {
        const processedFiles = await Promise.all(
          acceptedFiles.map(async (file) => {
            if (file.size > maxSize) {
              throw new Error(
                translate("file_exceeds_max_size",{name:file.name, size:maxSize / (1024 * 1024)})
              )
            }
            let processedFile = file
            if (file.name.toLowerCase().endsWith('.heic')) {
              const response = await convertHeicToJpeg(file)
              if (response) {
                processedFile = response
              }
            }

            return processedFile
          })
        )
        if (typeof processedFiles !== 'undefined') {
          onUpload(processedFiles)
        }
      } catch (err) {
        fireAlert({
          ...baseAlertConfig,
          message:
            err instanceof Error ? err.message : 'Error processing files',
        })
      }
    },
    [maxSize, onUpload]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: allowMultiple,
    accept: {
      'image/*': getAcceptedFormats(),
    },
  })

  return (
    <div className="w-full space-y-4">
      {isLoadingHeic ? (
        <LoadingCard isLoading={true}>{translate('loading_image')}</LoadingCard>
      ) : (
        <div
          {...getRootProps()}
          className={`border-2 bg-gray-100 border-gray-200 min-h-64 flex items-center justify-center flex-col border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
        >
          <input {...getInputProps()} />
          <div>
            <FileUploadIcon className="mx-auto text-xl text-gray-400" />
            <p className="mt-2 text-base font-medium text-gray-600">
              {translate(isMobile() ? 'take_photo' : 'drag_and_drop')}
            </p>
            <p className="text-sm text-gray-600">
              {translate('max_file_size', { max: maxSize / (1024 * 1024) })}
            </p>
          </div>
          {!isMobile() ? (
            <Button className="mt-4 w-full max-w-64" variant={'primary'}>
              {translate('browse_files')}
            </Button>
          ) : (
            <div className='mt-4'>
              <MobileButton size="md">
                <CameraSVG
                  className="m-2 w-20 h-20"
                  accentPathColor="white"
                  outlinePathColor="white"
                />
              </MobileButton>
            </div>
          )}
        </div>
      )}
      <div>
        <div className="mt-10">
          {uploadInformation}

          <div className="mt-6">
            <div className="flex items-center">
              <div className="w-5 flex justify-center">
                <StackIcon />
              </div>
              <div className="ml-4 text-sm">
                {translate('place_it_flat_surface')}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center">
              <div className="w-5 flex justify-center">
                <SparkleIcon />
              </div>
              <div className="ml-4 text-sm">
                {translate('avoid_glare_shaking')}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center">
              <div className="w-5 flex justify-center">
                <CameraIconBlack className="text-black" />
              </div>
              <div className="ml-4 text-sm">{translate('hold_the_camera')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DocumentUploadNew
